import { KIT_SUPPORT_EMAIL } from "./kitSubscribe";

/** Content type for the Join invite popup. One active item drives copy + timing. */
export const JOIN_INVITE_TYPE = "joinInvite";

export const DEFAULT_JOIN_DELAY_SECONDS = 12;
export const DEFAULT_JOIN_SCROLL_PERCENT = 50;
export const DEFAULT_JOIN_HIDE_DAYS = 30;

export const JOIN_INVITE_DEFAULTS = {
  showModal: true,
  headline: "Midnight in Warsaw is coming.",
  lead: "Be first to hear it. One list. Release-day alert. No spam.",
  button: "Join the founding list",
  legal: `By joining, you agree to emails from Ignite Elysium about releases and updates. Unsubscribe anytime. Questions: ${KIT_SUPPORT_EMAIL}`,
  success: "Almost there. Check your inbox to confirm your spot.",
  dismissLabel: "Not now",
  emailPlaceholder: "Email address",
  coverImageUrl: "",
  coverImageAlt: "E-0",
  coverCatalogMatch: "E-0",
  delaySeconds: String(DEFAULT_JOIN_DELAY_SECONDS),
  scrollPercent: String(DEFAULT_JOIN_SCROLL_PERCENT),
  hideDays: String(DEFAULT_JOIN_HIDE_DAYS),
  showCover: true,
  showLogo: true,
  showLegal: true,
  showDismissLink: true,
};

export const JOIN_INVITE_FIELD_KEYS = Object.keys(JOIN_INVITE_DEFAULTS);

export const JOIN_INVITE_TRUE_DEFAULTS = new Set([
  "showModal",
  "showCover",
  "showLogo",
  "showLegal",
  "showDismissLink",
]);

export const JOIN_INVITE_BOOLEAN_FIELDS = [
  "showModal",
  "showCover",
  "showLogo",
  "showLegal",
  "showDismissLink",
];

export const JOIN_INVITE_NUMBER_FIELDS = {
  delaySeconds: { min: 0, max: 120, step: 1 },
  scrollPercent: { min: 0, max: 100, step: 1 },
  hideDays: { min: 0, max: 365, step: 1 },
};

export const JOIN_INVITE_URL_FIELDS = ["coverImageUrl"];

export const JOIN_INVITE_FIELD_META = {
  showModal: {
    group: "timing",
    label: "Show the invite modal",
    hint: "Uncheck to hide the popup. Header, page, and footer Join stay available.",
  },
  delaySeconds: {
    group: "timing",
    label: "Delay seconds",
    hint: "How long to wait before showing, if they have not scrolled yet. Blank uses 12 (about 10–15).",
  },
  scrollPercent: {
    group: "timing",
    label: "Scroll percent",
    hint: "Show once they have scrolled this far down the page. Blank uses 50.",
  },
  hideDays: {
    group: "timing",
    label: "Hide for days",
    hint: "After close or a successful join, stay hidden this many days. 0 = until they close the tab. Blank uses 30.",
  },
  headline: {
    group: "copy",
    label: "Headline",
  },
  lead: {
    group: "copy",
    label: "Line",
    textarea: true,
  },
  button: {
    group: "copy",
    label: "Button",
  },
  emailPlaceholder: {
    group: "copy",
    label: "Email placeholder",
  },
  legal: {
    group: "copy",
    label: "Small print",
    textarea: true,
    hint: "Email addresses in this line become mailto links.",
  },
  success: {
    group: "copy",
    label: "Success",
    textarea: true,
    hint: "Shown in the modal after Kit accepts the signup.",
  },
  dismissLabel: {
    group: "copy",
    label: "Dismiss link",
  },
  coverImageUrl: {
    group: "look",
    label: "Cover image URL",
    hint: "E-0 cover (or any art). Leave blank to use a catalog item whose title matches the lookup below.",
  },
  coverImageAlt: {
    group: "look",
    label: "Cover alt text",
  },
  coverCatalogMatch: {
    group: "look",
    label: "Catalog cover lookup",
    hint: "If the URL is blank, use artwork from a catalog item whose title or slug contains this (default E-0).",
  },
  showCover: {
    group: "look",
    label: "Show the cover art",
  },
  showLogo: {
    group: "look",
    label: "Show the logo",
  },
  showLegal: {
    group: "copy",
    label: "Show the small print",
  },
  showDismissLink: {
    group: "copy",
    label: "Show the Not now link",
  },
};

export const JOIN_INVITE_GROUPS = [
  {
    id: "copy",
    title: "Words",
    hint: "Locked starting copy for the invite. Blank fields keep these defaults.",
  },
  {
    id: "look",
    title: "Look",
    hint: "E-0 cover on the left (dimmed behind the panel on small screens). Standard logo at the top of the panel.",
  },
  {
    id: "timing",
    title: "When it appears",
    hint: "Shows after the delay or once they scroll far enough; whichever happens first. Closing or joining hides it.",
  },
];

const asFlag = (value, fallback) => {
  if (value == null || value === "") return fallback;
  if (value === true || value === 1) return true;
  if (value === false || value === 0) return false;
  const v = String(value).trim().toLowerCase();
  if (v === "true" || v === "1" || v === "yes") return true;
  if (v === "false" || v === "0" || v === "no") return false;
  return fallback;
};

const asText = (value, fallback) => {
  if (value == null) return fallback;
  const s = String(value).trim();
  return s === "" ? fallback : s;
};

const parseClampedNumber = (value, fallback, min, max) => {
  if (value == null || String(value).trim() === "") return fallback;
  const n = Number(value);
  if (!Number.isFinite(n) || n < min) return fallback;
  return Math.min(max, n);
};

export const isJoinInviteType = (type) =>
  String(type || "")
    .trim()
    .toLowerCase() === JOIN_INVITE_TYPE.toLowerCase();

export const JOIN_INVITE_PACK_PREFIX = "ms1-join-invite:";

export const packJoinInviteIntoItem = (item) => {
  if (!item || typeof item !== "object") return item;
  if (!isJoinInviteType(item.type)) return item;
  const packed = {};
  JOIN_INVITE_FIELD_KEYS.forEach((key) => {
    packed[key] = item[key] == null ? "" : item[key];
  });
  return {
    ...item,
    text: JOIN_INVITE_PACK_PREFIX + JSON.stringify(packed),
  };
};

export const unpackJoinInviteFromItem = (item) => {
  if (!item || typeof item !== "object") return item;
  if (!isJoinInviteType(item.type)) return item;
  const raw = item.text;
  if (typeof raw !== "string" || !raw.startsWith(JOIN_INVITE_PACK_PREFIX)) {
    return item;
  }
  try {
    const packed = JSON.parse(raw.slice(JOIN_INVITE_PACK_PREFIX.length));
    if (!packed || typeof packed !== "object" || Array.isArray(packed)) {
      return item;
    }
    const next = { ...item };
    // Packed `text` is the source of truth. Extra keys can stick on the
    // document after create and then be ignored by a strict schema on
    // update; preferring those leftovers would freeze the first save.
    JOIN_INVITE_FIELD_KEYS.forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(packed, key)) {
        next[key] = packed[key];
      }
    });
    return next;
  } catch {
    return item;
  }
};

export const findJoinInviteItem = (content) => {
  if (!content || typeof content !== "object") return null;
  const items = Object.values(content).filter((item) =>
    isJoinInviteType(item?.type),
  );
  if (!items.length) return null;
  const active = items.find((item) => asFlag(item.active, false));
  return active ? unpackJoinInviteFromItem(active) : null;
};

const COVER_URL_KEYS = [
  "imageOneURL",
  "imageURL",
  "coverURL",
  "artworkURL",
  "image",
  "artwork",
  "cover",
];

const looksLikeUrl = (value) => {
  const s = String(value || "").trim();
  return /^https?:\/\//i.test(s);
};

export const findCatalogCoverUrl = (catalog, match = "E-0") => {
  const needle = String(match || "")
    .trim()
    .toLowerCase();
  if (!needle || !catalog || typeof catalog !== "object") return "";
  const items = Object.values(catalog);
  const hit =
    items.find((item) => {
      if (!item || typeof item !== "object") return false;
      const hay = [item.title, item.slug, item.name, item.identifier]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(needle);
    }) || null;
  if (!hit) return "";
  for (const key of COVER_URL_KEYS) {
    if (looksLikeUrl(hit[key])) return String(hit[key]).trim();
  }
  return "";
};

/** Merge an optional CMS item onto hardcoded defaults. Blank fields keep defaults. */
export const resolveJoinInvite = (content, catalog) => {
  const item = findJoinInviteItem(content) || {};
  const d = JOIN_INVITE_DEFAULTS;
  const coverFromItem = asText(item.coverImageUrl, "");
  const catalogMatch = asText(item.coverCatalogMatch, d.coverCatalogMatch);
  const coverFromCatalog = coverFromItem
    ? ""
    : findCatalogCoverUrl(catalog, catalogMatch);
  return {
    enabled: asFlag(item.showModal, d.showModal),
    headline: asText(item.headline, d.headline),
    lead: asText(item.lead, d.lead),
    button: asText(item.button, d.button),
    legal: asText(item.legal, d.legal),
    success: asText(item.success, d.success),
    dismissLabel: asText(item.dismissLabel, d.dismissLabel),
    emailPlaceholder: asText(item.emailPlaceholder, d.emailPlaceholder),
    coverImageUrl: coverFromItem || coverFromCatalog,
    coverImageAlt: asText(item.coverImageAlt, d.coverImageAlt),
    coverCatalogMatch: catalogMatch,
    delaySeconds: parseClampedNumber(
      item.delaySeconds,
      DEFAULT_JOIN_DELAY_SECONDS,
      0,
      120,
    ),
    scrollPercent: parseClampedNumber(
      item.scrollPercent,
      DEFAULT_JOIN_SCROLL_PERCENT,
      0,
      100,
    ),
    hideDays: parseClampedNumber(item.hideDays, DEFAULT_JOIN_HIDE_DAYS, 0, 365),
    showCover: asFlag(item.showCover, d.showCover),
    showLogo: asFlag(item.showLogo, d.showLogo),
    showLegal: asFlag(item.showLegal, d.showLegal),
    showDismissLink: asFlag(item.showDismissLink, d.showDismissLink),
  };
};
